import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Error boundary that catches chunk loading errors (dynamic imports)
 * and automatically reloads the page to get the latest version.
 *
 * This handles the case where a deployment happens while a user has
 * the app open, and the old chunk hashes no longer exist on the server.
 */
export class ChunkLoadErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State | null {
    // Check if this is a chunk loading error
    if (
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Loading chunk') ||
      error.message.includes('Loading CSS chunk') ||
      error.name === 'ChunkLoadError'
    ) {
      return { hasError: true };
    }
    return null;
  }

  componentDidCatch(error: Error) {
    // Check if this is a chunk loading error
    const isChunkError =
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Loading chunk') ||
      error.message.includes('Loading CSS chunk') ||
      error.name === 'ChunkLoadError';

    if (isChunkError) {
      // Check if we've already tried reloading recently (prevent infinite loops)
      const lastReload = sessionStorage.getItem('chunk-error-reload');
      const now = Date.now();

      if (!lastReload || now - parseInt(lastReload, 10) > 10000) {
        // Store the reload timestamp
        sessionStorage.setItem('chunk-error-reload', now.toString());
        // Force a hard reload to get the latest chunks
        window.location.reload();
      }
    }
  }

  render() {
    if (this.state.hasError) {
      // Fallback UI while reloading
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="text-center p-8">
            <div className="animate-spin w-8 h-8 border-4 border-cyan-600 border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">
              Az alkalmazás frissül...
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
